import { FinnairServiceItemWithUpsell, ServiceStatus } from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
} from '@fcom/dapi/api/models';
import { isFinnairNorraOrWetLease } from '@fcom/core/utils';

import { calculateIsIncludedAndQuantity, getServiceStatus, isNotIncludedService } from './common-booking.utils';
import { isFlight } from './flight.utils';

const isInfant = (passenger: FinnairPassengerItem) => passenger.passengerTypeCode === FinnairPassengerCode.INF;

export enum ServiceType {
  INCLUDED = 'included',
  NON_INCLUDED = 'noneIncluded',
  PAID = 'paid',
}

export interface ServiceSummaryTranslationKey {
  quantity?: number;
  description: string;
  text?: string;
  type?: ServiceType;
}

export interface FlightWithSeats {
  flight: string;
  seats: string[];
}

export const getSeatsByFlight = (
  passengers: FinnairPassengerItem[],
  currentBound: FinnairBoundItem,
  serviceItem: FinnairServiceItemWithUpsell,
  showPendingServices = false
): FlightWithSeats[] => {
  return currentBound.itinerary
    .filter(isFlight)
    .map((flight) => {
      const seats = passengers
        .filter((passenger) => !isInfant(passenger))
        .map((passenger) => {
          return (
            serviceItem.bounds
              .filter((bound) => bound.id === currentBound.id)
              .flatMap((bound) => bound.segments)
              .filter((serviceSegment) => serviceSegment.id === flight.id)
              .flatMap((serviceSegment) => serviceSegment.passengers)
              .filter((servicePax) => servicePax.id === passenger.id)
              .flatMap((servicePax) => servicePax.services)
              .filter((s) => getServiceStatus(s) === ServiceStatus.CONFIRMED || showPendingServices)
              .flatMap((servicePaxService) => servicePaxService.seatNumber)
              .filter((seatNumber) => seatNumber !== null && seatNumber.length > 0)?.[0] ?? '--'
          );
        });
      return {
        flight: `${flight.departure.locationCode}-${flight.arrival.locationCode}`,
        seats,
      };
    })
    .filter((seatInfo) => seatInfo.seats.length > 0);
};

export const getSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  showPendingServices: boolean
): ServiceSummaryTranslationKey => {
  if (service.category === Category.OTHER) {
    return { description: 'MMB.services.other' };
  }
  const { anyIncluded, totalQuantity } = calculateIsIncludedAndQuantity(service, currentBound, showPendingServices);

  const serviceCategory = resolveCategory(service.category);

  if (totalQuantity > 0) {
    return { quantity: totalQuantity, description: `MMB.services.${serviceCategory}.paid`, type: ServiceType.PAID };
  } else if (anyIncluded) {
    if ([Category.SPORT, Category.LOUNGE, Category.WIFI].includes(service.category) && totalQuantity === 0) {
      return { quantity: 1, description: `MMB.services.${serviceCategory}.included`, type: ServiceType.INCLUDED };
    }

    return { description: `MMB.services.${serviceCategory}.included`, type: ServiceType.INCLUDED };
  }
  return { description: `MMB.services.${serviceCategory}.noneIncluded`, type: ServiceType.NON_INCLUDED };
};

const getMealSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  isShortHaulFlight: boolean,
  showPendingServices: boolean
): ServiceSummaryTranslationKey => {
  const { anyIncluded, totalQuantity } = calculateIsIncludedAndQuantity(service, currentBound, showPendingServices);

  const serviceCategory = resolveCategory(service.category);

  if (totalQuantity > 0) {
    return { quantity: totalQuantity, description: `MMB.services.${serviceCategory}.paid`, type: ServiceType.PAID };
  }

  if (anyIncluded && !isShortHaulFlight) {
    return { description: `MMB.services.${serviceCategory}.included`, type: ServiceType.INCLUDED };
  }
  return { description: `MMB.services.${serviceCategory}.noneIncluded`, type: ServiceType.NON_INCLUDED };
};

const getViewServiceSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell
): ServiceSummaryTranslationKey => {
  return isServicePending(service, currentBound)
    ? { description: 'MMB.services.viewServiceSummary.pending' }
    : { description: 'MMB.services.viewServiceSummary.confirmed' };
};

function isServicePending(service: FinnairServiceItemWithUpsell, currentBound: FinnairBoundItem): boolean {
  return service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .flatMap((pax) => pax.services)
    .some((paxService) => isNotIncludedService(paxService) && !paxService.ticketed);
}

// Baggage allowances are only stated as exact amounts for flights Finnair itself operates; for anyone else the
// summary points the customer at the operating airline instead. Norra and wet-lease flights count as Finnair
// here, matching how the rest of the booking flow decides what is Finnair-operated.
const someFlightIsNotOperatedByFinnairGroup = (currentBound: FinnairBoundItem): boolean =>
  currentBound.itinerary
    .filter(isFlight)
    .some(
      (flight: FinnairItineraryItemFlight) =>
        !isFinnairNorraOrWetLease(flight.operatingAirline?.name, flight.operatingAirline?.code)
    );

export const getSummaryForCategory = (
  category: Category,
  bound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  isShortHaulFlight: boolean,
  showPendingServices = false
): ServiceSummaryTranslationKey[] => {
  switch (category) {
    case Category.CABIN_BAGGAGE:
      return getCabinBaggageSummary(bound, service, showPendingServices);
    case Category.BAGGAGE:
      return getCheckedBaggageSummary(bound, service, showPendingServices);
    case Category.MEAL:
      return [getMealSummary(bound, service, isShortHaulFlight, showPendingServices)];
    case Category.CHILD:
    case Category.FIREARM:
      return [getViewServiceSummary(bound, service)];
    case Category.WIFI:
    case Category.LOUNGE:
    case Category.SPORT:
    case Category.PET:
    case Category.PRIORITY:
    case Category.SPECIAL_NEED:
    case Category.COVER:
    case Category.SAF:
    case Category.SEAT_WIFI:
    case Category.SEAT_MEAL:
    case Category.SEAT_CABIN_BAGGAGE:
    case Category.SEAT_PRIORITY:
    case Category.LOUNGE_CABIN_BAGGAGE:
    case Category.LOUNGE_PRIORITY:
    case Category.LOUNGE_WIFI:
    case Category.WIFI_MEAL:
    case Category.OTHER:
      return service?.bounds ? [getSummary(bound, service, showPendingServices)] : [];
    default:
      return [];
  }
};

const EXCLUDE_FROM_SUMMARY_CABIN_BAGGAGE_VARIANTS = [
  'CABIN_WEIGHT',
  'CABIN_WEIGHT_COMBINED_OLD',
  'CABIN_WEIGHT_COMBINED',
];

const getCabinBaggageSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  showPendingServices: boolean
): ServiceSummaryTranslationKey[] => {
  if (someFlightIsNotOperatedByFinnairGroup(currentBound)) {
    return [{ description: 'MMB.services.baggage.summary-title' }];
  }

  const segments = service.bounds.find((b) => b.id === currentBound.id)?.segments.length ?? 1;
  return service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .flatMap((pax) => pax.services)
    .filter(
      (s) =>
        !EXCLUDE_FROM_SUMMARY_CABIN_BAGGAGE_VARIANTS.includes(s.variant) &&
        (getServiceStatus(s) === ServiceStatus.CONFIRMED || showPendingServices)
    )
    .reduce((all, s) => {
      const current = all.find((oldService) => oldService.variant === s.variant);

      if (current) {
        current.quantity = current.quantity + (s.quantity || 0);
      } else {
        all.push({ ...s });
      }

      return all;
    }, [])
    .map((s) => {
      const quantity = Math.ceil(s.quantity / segments);

      if (quantity === 0) {
        return { description: `MMB.services.cabinBaggage.${s.variant.toLowerCase()}` };
      }

      return {
        quantity,
        description: `MMB.services.cabinBaggage.amount.${s.variant.toLowerCase()}`,
      };
    });
};

const getPaxServiceDisplayName = (paxService: FinnairPassengerServiceSelectionItem): string => {
  return paxService.parameters?.baggageWeight > 0
    ? paxService.displayName.replace('{weight}', '' + paxService.parameters.baggageWeight)
    : paxService.displayName;
};

const normalizeDisplayNames = (service: FinnairPassengerServiceSelectionItem): FinnairPassengerServiceSelectionItem => {
  if (!service.includedInTierBenefit) {
    return {
      ...service,
      displayName: getPaxServiceDisplayName(service),
    };
  }

  const startIndex = service.displayName.indexOf('x') <= 2 ? 3 : 0;
  const endIndex = service.displayName.indexOf(',', startIndex);

  const trimmedDisplayName = service.displayName
    .slice(startIndex, endIndex !== -1 ? endIndex : service.displayName.length)
    .trim();

  return {
    ...service,
    displayName: trimmedDisplayName.charAt(0).toUpperCase() + trimmedDisplayName.slice(1),
  };
};

const getCheckedBaggageSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  showPendingServices: boolean
): ServiceSummaryTranslationKey[] => {
  if (someFlightIsNotOperatedByFinnairGroup(currentBound)) {
    return [{ description: 'MMB.services.baggage.summary-title' }];
  }

  const segments = service.bounds.find((b) => b.id === currentBound.id)?.segments.length ?? 1;
  const services = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .flatMap((pax) => pax.services)
    .filter((s) => getServiceStatus(s) === ServiceStatus.CONFIRMED || showPendingServices)
    .map(normalizeDisplayNames)
    .reduce((all, s) => {
      const current = all.find((oldService) => oldService.displayName === s.displayName);

      if (current) {
        current.quantity = current.quantity + (s.quantity || 0);
      } else {
        all.push({ ...s });
      }

      return all;
    }, [])
    .map((s) => ({
      quantity: Math.ceil(s.quantity / segments),
      description: '',
      text: s.displayName,
    }));

  if (services.length === 0) {
    return [{ description: 'MMB.services.baggage.noneIncluded', type: ServiceType.NON_INCLUDED }];
  }

  return services;
};

export const resolveCategory = (category: Category): string => {
  switch (category) {
    case Category.MEAL:
      return 'meals';
    case Category.BAGGAGE:
      return 'bags';
    default:
      return category;
  }
};
